import type { ContractOrgInvitationPublicView } from "@closer/shared";
import { findPermission, RECORD_ACCESS_LEVEL_NAMES } from "@closer/shared";
import { Badge, Button, Card, Spinner, useToast } from "@closer/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";

/**
 * Sözleşme tarafı daveti — token'lı görüntüleme sayfası (CLOS-1132 · Halit kararı 2).
 *
 * ### ⚠ GÖSTERİR, KABUL ETMEZ
 *
 * Diğer üç token sayfası (`/davet`, `/katilimci-davet`, `/temsilci-davet`) kararı burada
 * verdirir; bu sayfa vermez. Davet bir ADRESE geldi ama kabul bir ORGANİZASYON adına
 * verilecek (K1) ve o seçim yalnız Davetlerim'de yapılıyor — raporun *"acceptance yalnız
 * Profil > Davetlerim'de"* kuralı. Burada bir "Kabul et" düğmesi, kullanıcıya hangi
 * organizasyon adına kabul ettiğini sormadan temsil kararı verdirirdi.
 *
 * ### Oturum GEREKTİRMİYOR
 *
 * Davetli henüz hesap açmamış olabilir; kime ve hangi sözleşmeye çağrıldığını kaydolmadan
 * önce görebilmeli (sunucu ucu `GET /api/org-invitations/view` de oturumsuz). Rota bu
 * yüzden `RequireAuth` ağacının DIŞINDA; giriş/kayıt bağlantıları token'ı `?sozlesme=` ile
 * taşır ve hesap adımı bitince buraya geri dönülür (`inviteReturnPath`).
 */

type Durum = "yukleniyor" | "hazir" | "gecersiz";

export function ContractOrgInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user } = useAuth();

  const [davet, setDavet] = useState<ContractOrgInvitationPublicView | null>(null);
  const [durum, setDurum] = useState<Durum>(token ? "yukleniyor" : "gecersiz");

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const { data } = await api<ContractOrgInvitationPublicView>(
          `/api/org-invitations/view?token=${encodeURIComponent(token)}`,
        );
        /**
         * Sonuçlanmış ya da süresi geçmiş davet de "geçersiz" — sunucu var olmayan davetle
         * aynı cevabı vermiyor (satır var) ama kullanıcının atacağı adım aynı: yeni davet
         * istemek. Bekleyen olmayan bir daveti "hazır" göstermek yanıltırdı.
         */
        const gecerli =
          data.status === "pending" && new Date(data.expiresAt).getTime() > Date.now();
        setDavet(data);
        setDurum(gecerli ? "hazir" : "gecersiz");
      } catch {
        setDurum("gecersiz");
      }
    })();
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <Card title="Sözleşme Daveti" className="w-full max-w-md">
        {durum === "yukleniyor" && (
          <div role="status" className="flex flex-col items-center gap-3 py-4">
            <Spinner size="lg" />
            <p className="text-sm text-fg-muted">Davet kontrol ediliyor…</p>
          </div>
        )}

        {durum === "gecersiz" && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Bu davet geçerli değil. Bağlantının süresi dolmuş, davet iptal edilmiş ya da daha önce
              yanıtlanmış olabilir. Sizi davet eden organizasyondan yeni bir davet isteyebilirsiniz.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {durum === "hazir" && davet && (
          <DavetKarti
            davet={davet}
            token={token ?? ""}
            kullaniciEposta={user?.email ?? null}
            epostaDogrulandi={user?.emailVerified ?? false}
          />
        )}
      </Card>
    </div>
  );
}

function DavetKarti({
  davet,
  token,
  kullaniciEposta,
  epostaDogrulandi,
}: {
  davet: ContractOrgInvitationPublicView;
  token: string;
  kullaniciEposta: string | null;
  epostaDogrulandi: boolean;
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  /**
   * Yanlış hesapla girişli kullanıcının kaçış yolu (`InvitationPage` ile aynı): davet
   * başka adrese aitse Davetlerim'de görünmez; çıkıp doğru hesapla girebilmeli, token
   * `/login`'e taşınır ki giriş sonrası buraya dönsün.
   */
  const hesapDegistir = async (): Promise<void> => {
    try {
      await logout();
      navigate(`/login?sozlesme=${encodeURIComponent(token)}`);
    } catch {
      toast.error("Çıkış yapılamadı. Lütfen tekrar deneyin.");
    }
  };

  const donus = `sozlesme=${encodeURIComponent(token)}`;

  const baslik = (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-fg-muted">
        <strong className="text-fg">{davet.invitingOrganizationName}</strong>,{" "}
        {davet.principalKind === "person" ? "sizi" : "organizasyonunuzu"}
        {davet.partyDisplayName ? ` (${davet.partyDisplayName} adına)` : ""}{" "}
        <strong className="text-fg">{davet.contractTitle}</strong> sözleşmesinde ortak çalışmaya
        davet etti.
      </p>
      <div className="flex flex-wrap items-center gap-2 text-sm text-fg-soft">
        <span>Erişim düzeyi:</span>
        <Badge tone={davet.accessLevel === "write" ? "warning" : "neutral"}>
          {RECORD_ACCESS_LEVEL_NAMES[davet.accessLevel]}
        </Badge>
      </div>
      {/* Verilecek yetkiler ortak KATALOGDAN — Gelen Davetler ekranıyla aynı ifade. */}
      <p className="text-sm text-fg-soft" data-testid="davet-yetkileri">
        Verilecek yetkiler:{" "}
        {davet.permissions.map((k) => findPermission(k)?.description ?? k).join(" · ")}
      </p>
      <p className="text-sm text-fg-soft">
        Son yanıt tarihi:{" "}
        {new Date(davet.expiresAt).toLocaleDateString("tr-TR", { dateStyle: "medium" })}
      </p>
    </div>
  );

  if (!kullaniciEposta) {
    return (
      <div className="flex flex-col gap-4">
        {baslik}
        <p className="text-sm text-fg-muted">
          Daveti yanıtlamak için <strong>{davet.email}</strong> adresiyle giriş yapın. Bu adresle
          hesabınız yoksa önce kayıt olun; işlem bitince bu sayfaya geri dönersiniz.
        </p>
        <Link
          to={`/login?${donus}`}
          className="text-center font-medium text-primary hover:underline"
        >
          Giriş yap
        </Link>
        <Link
          to={`/register?${donus}`}
          className="text-center font-medium text-primary hover:underline"
        >
          Kayıt ol
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {baslik}

      <p role="status" className="text-sm text-fg-muted">
        Bu davet <strong>{davet.email}</strong> adresine gönderildi. Şu an{" "}
        <strong>{kullaniciEposta}</strong> ile giriştesiniz.
      </p>

      {/*
        ⚠ KARAR BURADA DEĞİL: kabul, hangi organizasyon adına verileceği seçilerek
        Davetlerim'de yapılır. Buradan yalnız oraya gidilir.
      */}
      <p className="text-sm text-fg-muted">
        Daveti kabul ederken hangi organizasyonunuz adına katılacağınızı seçeceksiniz; bu seçim
        Davetlerim sayfasında yapılır.
      </p>

      {!epostaDogrulandi && (
        <p className="text-sm text-fg-muted">
          Daveti yanıtlamak için önce e-posta adresinizi doğrulamalısınız.{" "}
          <Link to="/verify-email" className="font-medium text-primary hover:underline">
            E-postamı doğrula
          </Link>
        </p>
      )}

      <Button onClick={() => navigate("/davetlerim")}>Davetlerim&apos;e git</Button>

      <p className="text-center text-sm text-fg-soft">
        Bu hesap değil mi?{" "}
        <Button variant="ghost" onClick={() => void hesapDegistir()}>
          Farklı hesapla giriş yap
        </Button>
      </p>
    </div>
  );
}
